import { useEffect, useState, useRef } from 'react';
import { Tile, Col, Form, Button, Action, Row } from '@op/opds.opux';
import { IconRemove } from '@op/opds.opux-icons/action';
import message from '../messages/Message';
import InputRow from '../components/InputRow';
import { formatResultNumber } from '../utils/formatter';
import { resetButtonSendAnalytics, trackBonusCalculatorPhase, trackBonusCalculatorCompletedPhase } from '../utils/bonusCalculatorAnalytics';
import { sumRanges } from '../utils/sumRanges';
import '../App.css';

const Bonuscalculator = () => {
  const [loans, setLoans] = useState(0);
  const [investments, setInvestments] = useState(0);
  const [savings, setSavings] = useState(0);
  const [premiums, setPremiums] = useState(0);
  const [unsecuredLoans, setUnsecuredLoans] = useState(0);
  const [ariaLiveMessage, setAriaLiveMessage] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [calculation, setCalculation] = useState({
    totalTaxedBonusWithPremium: 0,
    monthlyBonusIncreased: 0
  });
  const [resultsAriaLiveMessage, setResultsAriaLiveMessage] = useState('');
  const [calculationCount, setCalculationCount] = useState(0);


  // 2026 premium bonus rate (was 0.004 = 0.4%)
  const PREMIUM_BONUS_RATE = 0.05;
  const maxInputValue = 9999999;
  const firstInputRef = useRef(null);

  const handleInputFocus = (e) => {
    e.target.select();
  };

  // Hide result when user changes any input
  const handleInputChange = (event, setBonus) => {
    const value = event.target.value;

    // Hide result when user changes any input
    setShowResult(false);

    // Prevent minus sign from being entered
    if (value.includes('-')) {
      return;
    }

    if (value === "") {
      setBonus(0);
      return;
    }

    const parsedValue = parseInt(value);

    if (!isNaN(parsedValue) && parsedValue <= maxInputValue) {
      setBonus(parsedValue);

    }

    if (!isNaN(parsedValue) && parsedValue >= maxInputValue) {
      setBonus(maxInputValue);
    }
  };

  const handleBtnChange = (e, amount, inputField) => {
    setShowResult(false); // Hide result when value is changed by button

    inputField((prevValue) => {
      const value = prevValue + amount;
      // Prevent the value from going below zero
      const noNegativeValue = value < 0 ? 0 : value;
      // Prevent the value from going over max
      const finalValue = noNegativeValue >= maxInputValue ? maxInputValue : noNegativeValue;
      const localizedMessage = message('value_announcement', { value: finalValue });

      setAriaLiveMessage(localizedMessage);
      setTimeout(() => setAriaLiveMessage(''), 3000); // Clear the message after 3 seconds
      return finalValue;
    });
  };

  const getSumRangeLabels = (values) => {
    const labels = [];
    for (let i = 0; i < values.length; i++) {
      const value = values[i];
      let range;
      if (i === 0) { // investments input field
        range = sumRanges.loans.find(range => value >= range.min && value <= range.max);
      } else if (i === 1) { // savings input field
        range = sumRanges.unsecuredloans.find(range => value >= range.min && value <= range.max);
      } else if (i === 2) { // premiums input field
        range = sumRanges.investments.find(range => value >= range.min && value <= range.max);
      } else if (i === 3) { // loans input field
        range = sumRanges.savings.find(range => value >= range.min && value <= range.max);
      } else if (i === 4) { // unsecured loans input field
        range = sumRanges.premiums.find(range => value >= range.min && value <= range.max);
      } else { //estimated bonus field
        range = sumRanges.estimatedbonuses.find(range => value >= range.min && value <= range.max);
      }
      const label = range ? range.label : 'Unknown';
      labels.push(label);
    }
    return labels;

  };

  const resetButton = () => {
    resetButtonSendAnalytics.complete(document.documentElement.getAttribute('lang'));
    const componentContainer = document.getElementById('Bonuscalculator');

    if (componentContainer) {
      componentContainer.scrollIntoView({ behavior: 'smooth' });
    }

    setLoans(0);
    setSavings(0);
    setPremiums(0);
    setInvestments(0);
    setUnsecuredLoans(0);

    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
    setShowResult(false); // Hide the result when resetting
  };

  // Calculate bonus only when Laske is clicked
  const calculateBonus = () => {
    const totalAmount = loans + investments + savings + unsecuredLoans;
    const premiumNoTax = premiums * PREMIUM_BONUS_RATE;
    const totalBonusNew = totalAmount * 0.004;
    const taxedBonusAmount = totalBonusNew * 0.7;
    const totalTaxedBonusWithPremium = taxedBonusAmount + premiumNoTax;
    const monthlyBonusIncreased = Number(totalTaxedBonusWithPremium / 12).toFixed(0);

    setCalculation({
      totalTaxedBonusWithPremium,
      monthlyBonusIncreased
    });
    setShowResult(true);
    setCalculationCount(count => count + 1);

    // Get sum range labels for all fields
    const sumRangeLabels = getSumRangeLabels([
      loans,
      unsecuredLoans,
      investments,
      savings,
      premiums,
      totalTaxedBonusWithPremium // estimated bonuses
    ]);


    // Send analytics to Adobe with sum range labels
    trackBonusCalculatorCompletedPhase({
      language: document.documentElement.getAttribute('lang'),
      loansSumRange: sumRangeLabels[0],
      unsecuredLoansSumRange: sumRangeLabels[1],
      investmentsSumRange: sumRangeLabels[2],
      savingsSumRange: sumRangeLabels[3],
      premiumsSumRange: sumRangeLabels[4],
      estimatedBonusesSumRange: sumRangeLabels[5]
    });
  };

  useEffect(() => {
    if (showResult) {
      setResultsAriaLiveMessage(message("results_calculated_aria_live"));
      const timeout = setTimeout(() => setResultsAriaLiveMessage(''), 1000);
      return () => clearTimeout(timeout);
    }
  }, [calculationCount, showResult]);

  useEffect(() => {
    trackBonusCalculatorPhase();
  }, []);

  return (
    <div className="opds App" id="Bonuscalculator">
      <section aria-labelledby="bonuscalculator-title" className="App-section">
        {/* Always present aria-live region for results */}
        <div aria-live="polite" style={{ position: 'absolute', left: '-9999px' }}>
          {resultsAriaLiveMessage}
        </div>
        <Col style={{ padding: 0 }}>
          <Tile title={message('app_header')} className="ds-tile" >
            <p>{message("app_ingressi")}</p>
            <Form stacked spacing="default" onSubmit={e => e.preventDefault()}>
              <Row className="formRow">
                <InputRow
                  tooltipMsg={message('stepper_loans_tooltip')}
                  select={handleInputFocus}
                  labelMsg={message('stepper_loans_label')}
                  btnAddValue={(e) => handleBtnChange(e, 1000, setLoans)}
                  btnDecValue={(e) => handleBtnChange(e, -1000, setLoans)}
                  value={loans}
                  setInputValue={(e) => handleInputChange(e, setLoans)}
                  inputRef={firstInputRef}
                  disableDecBtn={loans <= 0}
                  disableAddBtn={loans === maxInputValue}
                  btnAddName={message('btnAddNameLoan')}
                  btnDecName={message('btnDecNameLoan')}
                  inputRowId="inputRowLoans"
                  btnDecId="buttonDecLoans"
                  btnAddId="buttonAddLoans"
                  btnTestAddId="buttonAddLoans"
                  btnTestDecId="buttonDecLoans"
                  inputTestId="inputLoansTestId"
                />
                <InputRow
                  tooltipMsg={message('stepper_unsecured_loans_tooltip')}
                  select={handleInputFocus}
                  labelMsg={message('stepper_unsecured_loans_label')}
                  btnAddValue={(e) => handleBtnChange(e, 1000, setUnsecuredLoans)}
                  btnDecValue={(e) => handleBtnChange(e, -1000, setUnsecuredLoans)}
                  value={unsecuredLoans}
                  setInputValue={(e) => handleInputChange(e, setUnsecuredLoans)}
                  disableDecBtn={unsecuredLoans <= 0}
                  disableAddBtn={unsecuredLoans === maxInputValue}
                  btnAddName={message('btnAddNameUnsecuredLoans')}
                  btnDecName={message('btnDecNameUnsecuredLoans')}
                  inputRowId="inputRowUnsecuredLoans"
                  btnDecId="buttonDecUnsecuredLoans"
                  btnAddId="buttonAddUnsecuredLoans"
                  btnTestAddId="buttonAddUnsecuredLoans"
                  btnTestDecId="buttonDecUnsecuredLoans"
                  inputTestId="inputUnsecuredLoansTestId"
                />
                <InputRow
                  tooltipMsg={message('stepper_investments_tooltip')}
                  select={handleInputFocus}
                  labelMsg={message('stepper_investments_label')}
                  btnAddValue={(e) => handleBtnChange(e, 100, setInvestments)}
                  btnDecValue={(e) => handleBtnChange(e, -100, setInvestments)}
                  value={investments}
                  setInputValue={(e) => handleInputChange(e, setInvestments)}
                  disableDecBtn={investments <= 0}
                  disableAddBtn={investments === maxInputValue}
                  btnAddName={message('btnAddNameInv')}
                  btnDecName={message('btnDecNameInv')}
                  inputRowId="inputRowInvestments"
                  btnDecId="buttonDecInvestments"
                  btnAddId="buttonAddInvestments"
                  btnTestAddId="buttonAddInvestments"
                  btnTestDecId="buttonDecInvestments"
                  inputTestId="inputInvestmentsTestId"
                />
                <InputRow
                  tooltipMsg={message('stepper_savings_tooltip')}
                  select={handleInputFocus}
                  labelMsg={message('stepper_savings_label')}
                  btnAddValue={(e) => handleBtnChange(e, 100, setSavings)}
                  btnDecValue={(e) => handleBtnChange(e, -100, setSavings)}
                  value={savings}
                  setInputValue={(e) => handleInputChange(e, setSavings)}
                  disableDecBtn={savings <= 0}
                  disableAddBtn={savings === maxInputValue}
                  btnAddName={message('btnAddNameSav')}
                  btnDecName={message('btnDecNameSav')}
                  inputRowId="inputRowSavings"
                  btnDecId="buttonDecSavings"
                  btnAddId="buttonAddSavings"
                  btnTestAddId="buttonAddSavings"
                  btnTestDecId="buttonDecSavings"
                  inputTestId="inputSavingsTestId"
                />
                <InputRow
                  tooltipMsg={message('stepper_premiums_tooltip')}
                  select={handleInputFocus}
                  labelMsg={message('stepper_premiums_label')}
                  btnAddValue={(e) => handleBtnChange(e, 50, setPremiums)}
                  btnDecValue={(e) => handleBtnChange(e, -50, setPremiums)}
                  value={premiums}
                  setInputValue={(e) => handleInputChange(e, setPremiums)}
                  disableDecBtn={premiums <= 0}
                  disableAddBtn={premiums === maxInputValue}
                  btnAddName={message('btnAddNamePrem')}
                  btnDecName={message('btnDecNamePrem')}
                  inputRowId="inputRowPremiums"
                  btnDecId="buttonDecPremiums"
                  btnAddId="buttonAddPremiums"
                  btnTestAddId="buttonAddPremiums"
                  btnTestDecId="buttonDecPremiums"
                  inputTestId="inputPremiumsTestId"
                />
              </Row>
              <p>{message("secondary_help_text")}</p>
            </Form>
            <Row className="buttonRow">
              <Col className="buttonContainer" xs={12} md={12} lg={12}>
                <Action className="reset-button"
                  button
                  onClick={resetButton}
                  type="button"
                  icon={IconRemove}
                  iconPos="left"
                  data-testid="resetButton"
                >
                  {message('emptyCalculations')}
                </Action>
                <Button primary onClick={calculateBonus} data-testid="calculateButtonTestId">
                  {message('btnCalculateName')}
                </Button>
              </Col>
            </Row>
            {showResult && (
              <section className="sectionResult" aria-labelledby="results-heading">
                {/*<h2 id="results-heading" className="sr-only">{message('results_heading')}</h2>*/}
                <div id='resultArea'>
                  <div id='bonusBoxes'>
                    <div id="bonusBox">
                      <span id='bonusBoxHeader'>{message('bonusmessage_header_bonus')}</span>
                      {/* <span id='bonusBoxHeader2'>{message('bonusmessage_header_inc')}</span> */}
                      <span id="bonusBoxCalcIncreasedAmount" data-testid="bonusBoxCalcIncreasedAmountTestId">{calculation.totalTaxedBonusWithPremium === 0 ? "0" : formatResultNumber(calculation.totalTaxedBonusWithPremium)} €</span>
                      <span>{message("bonusmessage_eurosPerYear")}</span>
                      {calculation.totalTaxedBonusWithPremium >= 10 && <span>{message('bonusmessage_eurosPerMonth').replace('bonusInMonth', calculation.monthlyBonusIncreased)}</span>}
                    </div>
                  </div>
                  <div id="disclaimerArea">
                    <p id="disclaimer">{message('disclaimer_p1')}</p>
                    <p>{message('disclaimer_p2')}</p>
                    <p>{message('disclaimer_p3')}</p>
                    <p>{message('disclaimer_p4')}</p>
                    <p>{message('disclaimer_p5')}</p>
                    <p>{message('disclaimer_p6')}</p>
                  </div>
                </div>
              </section>
            )}
          </Tile>
        </Col>
        <div aria-live="polite" style={{ position: 'absolute', left: '-9999px' }}>
          {ariaLiveMessage}
        </div>
      </section>
    </div>
  );
};

export default Bonuscalculator;